/* globals FileReader */

'use strict'
import PhotoStub from 'images/photo_stub.png'
import { initCropper as createCropper } from 'lib/init_cropper'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import cn from 'classnames'

export default class PhotoWithCropper extends React.Component {
  static MAX_BASE64_IMAGE_BYTES = 2 * 1024 * 1024

  constructor(props) {
    super(props)
    this.default_state = {
      x: '',
      y: '',
      height: '',
      width: '',
      modified: false,
      modal_open: false,
      photo_url: props.photo_url,
      cropped_photo_url: '',
      to_remove: false,
      error: '',
      formSubmitted: false
    }
    this.state = this.default_state
    this.file_input = React.createRef()
    this.cropImage = React.createRef()
    this.cropper = undefined
    this.initializingCropper = false
    this.loadPhotoPreviewInProgress = false
    this.cropperInitToken = 0

    // Apply crop attributes
    if (props.crop_attributes) {
      const cropAttrs = props.crop_attributes
      if (cropAttrs.image_x) this.state.x = parseFloat(cropAttrs.image_x)
      if (cropAttrs.image_y) this.state.y = parseFloat(cropAttrs.image_y)
      if (cropAttrs.image_width) this.state.width = parseFloat(cropAttrs.image_width)
      if (cropAttrs.image_height) this.state.height = parseFloat(cropAttrs.image_height)
      if (cropAttrs.image_base64) {
        this.state.photo_url = cropAttrs.image_base64
        this.state.cropped_photo_url = cropAttrs.image_base64
      }
    }
  }

  dataUrlSizeBytes(dataUrl) {
    const base64Payload = (dataUrl.split(',')[1] || '')
    if (!base64Payload) return 0

    const padding = base64Payload.endsWith('==') ? 2 : (base64Payload.endsWith('=') ? 1 : 0)
    return Math.floor((base64Payload.length * 3) / 4) - padding
  }

  createCompressedDataUrl(canvas) {
    let quality = 0.92
    let dataUrl = canvas.toDataURL('image/jpeg', quality)

    while (
      this.dataUrlSizeBytes(dataUrl) > PhotoWithCropper.MAX_BASE64_IMAGE_BYTES &&
      quality > 0.4
    ) {
      quality = Math.max(0.4, quality - 0.08)
      dataUrl = canvas.toDataURL('image/jpeg', quality)
    }

    return dataUrl
  }

  nextFrame() {
    return new Promise(resolve => {
      window.requestAnimationFrame(resolve)
    })
  }

  async waitForImageLayout(img, initToken) {
    if (typeof img.decode === 'function') {
      try {
        await img.decode()
      } catch (error) {
        // Ignore decode failures and fall back to layout polling.
      }
    }

    let previousRect = null

    for (let attempt = 0; attempt < 10; attempt++) {
      if (initToken !== this.cropperInitToken) return null

      await this.nextFrame()

      const rect = img.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) continue

      if (
        previousRect &&
        previousRect.width === rect.width &&
        previousRect.height === rect.height
      ) {
        return rect
      }

      previousRect = rect
    }

    return previousRect
  }

  getImageDisplayHeight(img, rect) {
    if (!rect) return null

    if (img.naturalWidth > 0 && img.naturalHeight > 0 && rect.width > 0) {
      return rect.width * (img.naturalHeight / img.naturalWidth)
    }

    return rect.height
  }

  getImageAspectRatio(img) {
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      return `${img.naturalWidth} / ${img.naturalHeight}`
    }

    return null
  }

  initCropper() {
    if (this.initializingCropper) return

    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = undefined
    }

    this.initializingCropper = true
    const initToken = ++this.cropperInitToken

    const updateState = async (cropperSelection, cropperImage) => {
      const { x, y, width, height } = cropperSelection

      const imgRect = cropperImage.getBoundingClientRect()
      const scaleX = this.cropImage.current.naturalWidth / imgRect.width
      const scaleY = this.cropImage.current.naturalHeight / imgRect.height

      const scaledX = x * scaleX
      const scaledY = y * scaleY
      const scaledWidth = width * scaleX
      const scaledHeight = height * scaleY

      const canvas = await cropperSelection.$toCanvas({
        width: Math.round(scaledWidth),
        height: Math.round(scaledHeight)
      })

      const dataUrl = this.createCompressedDataUrl(canvas)

      if (this.dataUrlSizeBytes(dataUrl) > PhotoWithCropper.MAX_BASE64_IMAGE_BYTES) {
        this.setState({
          error: I18n.t('user_photo_size_error')
        })
        return
      }

      this.setState({
        cropped_photo_url: dataUrl,
        error: '',
        x: scaledX,
        y: scaledY,
        width: scaledWidth,
        height: scaledHeight,
        modified: true
      })
    }

    const img = this.cropImage.current
    if (!img) {
      this.initializingCropper = false
      return
    }

    const initWhenReady = async () => {
      if (initToken !== this.cropperInitToken) return

      try {
        const rect = await this.waitForImageLayout(img, initToken)
        if (initToken !== this.cropperInitToken) return

        const container = img.parentElement
        const aspectRatio = this.getImageAspectRatio(img)
        const displayHeight = this.getImageDisplayHeight(img, rect)
        if (container && aspectRatio) {
          container.style.width = '100%'
          container.style.height = 'auto'
          container.style.aspectRatio = aspectRatio
        } else if (container && displayHeight) {
          container.style.height = `${displayHeight}px`
        }

        img.crossOrigin = 'anonymous'
        const nextCropper = await createCropper({
          imgRef: img,
          aspectRatio: this.props.aspect_ratio || 1,
          ready: async ({ cropperSelection, cropperImage }) => {
            cropperImage.crossOrigin = 'anonymous'

            await this.nextFrame()

            if (initToken !== this.cropperInitToken) return

            await updateState(cropperSelection, cropperImage)
          },
          onCropEnd: async ({ cropperSelection, cropperImage }) => {
            await updateState(cropperSelection, cropperImage)
          }
        })

        if (initToken !== this.cropperInitToken) {
          nextCropper.destroy()
          return
        }

        this.cropper = nextCropper
      } finally {
        if (initToken === this.cropperInitToken) {
          this.initializingCropper = false
        }
      }
    }

    if (img.complete && img.naturalWidth > 0) {
      initWhenReady()
    } else {
      const onLoad = () => {
        img.removeEventListener('load', onLoad)
        initWhenReady()
      }

      img.addEventListener('load', onLoad, { once: true })
    }
  }

  render() {
    const { entity, removable, photo_upload_url: photoUploadUrl, model } = this.props
    const {
      x,
      y,
      width,
      height,
      photo_url: photoUrl,
      cropped_photo_url: croppedPhotoUrl,
      modified,
      error,
      to_remove: toRemove,
      modal_open: modalOpen,
      formSubmitted,
      uploadable
    } = this.state
    const cropperPhotoSrc = !photoUrl || toRemove ? PhotoStub : photoUrl
    const previewPhotoSrc = !photoUrl || toRemove ? PhotoStub : (croppedPhotoUrl || photoUrl)
    const cropButtonDisabled =
      !photoUrl || toRemove || this.cropper !== undefined || this.initializingCropper || this.loadPhotoPreviewInProgress

    return (
      <>
        <Photo
          photoSrc={previewPhotoSrc}
          onClick={() => this.setState({ modal_open: true })}
          cropRef={this.cropImage}
          asImg={false}
        />

        <Modal
          centered
          size='sm'
          show={modalOpen}
          onHide={this.resetButton.bind(this)}
        >
          <Modal.Body>
            <div>
              <Photo photoSrc={cropperPhotoSrc} cropRef={this.cropImage} asImg />
            </div>

            {error && <div className='alert alert-danger mt-3'>{error}</div>}

            <div className='flex full-width flex-wrap justify-content-center'>
              <Button onClick={this.uploadButton.bind(this)} className='flex-1 mt-3'>
                <i className='fa fa-upload fa-lg' />
                {I18n.t('upload_new_file')}
              </Button>
              <Button
                className='mt-3'
                variant='default'
                onClick={this.loadPhotoPreview.bind(this)}
                disabled={cropButtonDisabled}
              >
                <i className='fa fa-crop-alt fa-lg' />
                {I18n.t('layouts.actions.crop')}
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            {this.renderRemovable()}
            <Button variant='default' onClick={this.resetButton.bind(this)}>
              {modified ? I18n.t('cancel') : I18n.t('close')}
            </Button>

            {(modified || toRemove) && (
              <Button
                variant='success'
                onClick={this.doneButton.bind(this)}
                disabled={formSubmitted}
              >
                {I18n.t('done')}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <div className='hidden'>
          {uploadable && (
            <input
              type='file'
              accept='image/png,image/gif,image/jpeg'
              name='file-photo'
              data-direct-upload-url={photoUploadUrl}
              onChange={this.loadPhotoPreview.bind(this)}
              ref={(input) => (this.file_input = input)}
            />
          )}

          {modified && (
            <input type='hidden' name={`${entity}[${model}_cropping_attributes][image_x]`} value={x} readOnly aria-label={I18n.t('cropper.aria.image_x_axis')} />
          )}
          {modified && (
            <input type='hidden' name={`${entity}[${model}_cropping_attributes][image_y]`} value={y} readOnly aria-label={I18n.t('cropper.aria.image_y_axis')} />
          )}
          {modified && (
            <input type='hidden' name={`${entity}[${model}_cropping_attributes][image_width]`} value={width} readOnly aria-label={I18n.t('cropper.aria.image_width')} />
          )}
          {modified && (
            <input type='hidden' name={`${entity}[${model}_cropping_attributes][image_height]`} value={height} readOnly aria-label={I18n.t('cropper.aria.image_height')} />
          )}
          {modified && (
            <input type='hidden' name={`${entity}[${model}_cropping_attributes][image_base64]`} value={croppedPhotoUrl || photoUrl} readOnly aria-label={I18n.t('cropper.aria.image_base64')} />
          )}
          {removable && (
            <input
              type='hidden'
              name={`${this.props.entity}[remove_${this.props.model}]`}
              value={toRemove ? 1 : 0}
              readOnly
              aria-label={I18n.t('cropper.aria.remove_photo')}
            />
          )}
        </div>
      </>
    )
  }

  uploadButton() {
    this.setState(
      {
        uploadable: true
      },
      () => {
        this.file_input.type = 'file'
        this.file_input.click()
      }
    )
  }

  doneButton() {
    const { modified, error, to_remove: toRemove } = this.state
    const { submit } = this.props

    if (error) {
      this.setState(this.default_state)
      this.destroyCropper()
    } else if ((modified || toRemove) && typeof submit === 'string') {
      document.getElementById(submit).submit()
      this.setState({ formSubmitted: true })
    } else if ((modified || toRemove) && typeof submit === 'function') {
      submit()
      this.setState({ modal_open: false })
      this.destroyCropper()
    } else {
      this.setState({ modal_open: false })
      this.destroyCropper()
    }
  }

  resetButton() {
    this.setState(this.default_state)
    this.destroyCropper()
  }

  renderRemovable() {
    const { photo_url: photoUrl, to_remove: toRemove } = this.state
    if (this.props.removable && (photoUrl || toRemove)) {
      return (
        <label className='checkbox photo-upload-remove me-auto mt-0'>
          <input
            type='checkbox'
            checked={toRemove}
            onChange={this.removePhoto.bind(this)}
          />
          {I18n.t('remove_user_photo')}
          <span className='box' />
        </label>
      )
    }

    return null
  }

  removePhoto() {
    this.destroyCropper()
    this.setState({ to_remove: !this.state.to_remove })
  }

  loadPhotoPreview() {
    if (this.loadPhotoPreviewInProgress) return
    this.loadPhotoPreviewInProgress = true

    this.destroyCropper()
    if (this.file_input.files && this.file_input.files[0]) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.setState(
          {
            to_remove: false,
            error: '',
            photo_url: e.target.result,
            cropped_photo_url: ''
          },
          () => {
            this.loadPhotoPreviewInProgress = false
            this.initCropper()
          }
        )
      }
      const file = this.file_input.files[0]
      const filesize = (file.size / 1024 / 1024).toFixed(4)
      if (filesize > 1) {
        this.setState({
          error: I18n.t('user_photo_size_error'),
          photo_url: ''
        })
        this.loadPhotoPreviewInProgress = false
      } else {
        reader.readAsDataURL(file)
      }
    } else {
      this.setState(
        {
          photo_url: this.props.photo_url,
          cropped_photo_url: ''
        },
        () => {
          this.loadPhotoPreviewInProgress = false
          this.initCropper()
        }
      )
    }
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = undefined
    }

    const img = this.cropImage.current
    const container = img?.parentElement

    if (container) {
      container.style.aspectRatio = ''
      container.style.height = ''
      container.style.width = ''
    }
  }
}

function Photo(props) {
  const { photoSrc, onClick, cropRef, asImg } = props
  const handleKeyDown = (event) => {
    if (!onClick) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick(event)
    }
  }

  if (asImg) {
    return (
      <img
        src={photoSrc}
        alt={I18n.t('cropper.aria.profile_photo_preview')}
        onClick={onClick}
        className='img-fluid full-width'
        ref={cropRef}
      />
    )
  }

  return (
    <div
      className={cn('person-photo', { clickable: onClick })}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? I18n.t('cropper.aria.open_photo_editor') : undefined}
      style={{ backgroundImage: `url('${photoSrc}')` }}
    />
  )
}

PhotoWithCropper.propTypes = {
  entity: PropTypes.string.isRequired,
  photo_upload_url: PropTypes.string.isRequired,
  photo_url: PropTypes.string,
  aspect_ratio: PropTypes.string,
  crop_attributes: PropTypes.object,
  removable: PropTypes.bool.isRequired,
  model: PropTypes.string.isRequired,
  submit: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
}
